import { wsService } from '@/processes/WsService/model';
import {
  ApolloClient,
  FetchResult,
  HttpLink,
  InMemoryCache,
  Observable,
  from,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { RetryLink } from '@apollo/client/link/retry';
import { isNullabilityError, logger } from '@/shared/lib';

import { TokenDocument, TokenMutation } from './__generated__';
import { envConfig } from '@/shared/lib/env';

const WITHOUT_COMPANY_ID = [
  'addressCategories',
  'businessActivities',
  'changeIdentity',
  'changePasswordV2',
  'checkCode',
  'childMembers',
  'companyTotalShare',
  'confirmChangeIdentity',
  'confirmChangePassword',
  'confirmPasswordRecovery',
  'confirmRegisterIdentity',
  'confirmSignIn',
  'countriesV2',
  'createCorporateMembers',
  'createMyCompany',
  'currencies',
  'defaultCountry',
  'documentConfigs',
  'draftCompany',
  'executeKYBStep',
  'getFileInfo',
  'incomingPayments',
  'logout',
  'myCompanies',
  'myPersonalData',
  'outgoingPayments',
  'paymentInfoRequests',
  'registerIdentity',
  'sendCodeV2',
  'sendTFACode',
  'signInV2',
  'signUpV2',
  'startPasswordRecoveryV2',
  'token',
  'updatePersonal',
  'vabsAccMyVirtualBalances',
];

const WITHOUT_AUTH_HEADER = [];

const httpLink = new HttpLink({
  uri: envConfig.DEV ? '/graphql' : envConfig.VITE_BACKEND_URL,
});

const retryLink = new RetryLink({
  delay: {
    initial: 300,
    max: Infinity,
    jitter: true,
  },
  attempts: {
    max: 4,
  },
});

const authMiddleware = setContext((op, { headers }) => {
  const token = localStorage.getItem('@accessToken');
  const companyState = localStorage.getItem('@gatewayCompanyId');
  const temporaryCompanyId = localStorage.getItem('@temporaryCompanyId');
  const operationName = op.operationName || '';
  const skipCompanyId = WITHOUT_COMPANY_ID.includes(operationName);
  const skipAuthHeader = WITHOUT_AUTH_HEADER.includes(operationName);

  const currentHeaders = headers || {};

  const newHeaders = {
    ...currentHeaders,
    ...(skipAuthHeader ? {} : { authorization: token ? `Bearer ${token}` : '' }),
    timeout: 999999,
  };
  if (companyState) {
    const companyId = JSON.parse(companyState).state.companyId;
    if (
      (temporaryCompanyId || companyId) &&
      !WITHOUT_COMPANY_ID.includes(op.operationName || '')
    ) {
      newHeaders['gateway-company-id'] =
        currentHeaders['gateway-company-id'] || temporaryCompanyId || companyId;
    }
  }

  if (op.operationName === 'token')
    return {
      timeout: 999999,
    };
  return { headers: newHeaders };
});

let isRefreshing = false;

export const clearLs = () => {
  localStorage.removeItem('@expiresIn');
  localStorage.removeItem('@accessToken');
  localStorage.removeItem('@refreshToken');
  localStorage.removeItem('@gatewayCompanyId');
  localStorage.removeItem('@temporaryCompanyId');
};

export const getNewToken = async (): Promise<TokenMutation['token'] | undefined> => {
  const accessToken = localStorage.getItem('@accessToken');
  const refreshToken = localStorage.getItem('@refreshToken');

  if (!!refreshToken) {
      const res = await client
    .mutate<FetchResult<TokenMutation>['data']>({
      fetchPolicy: 'no-cache',
      mutation: TokenDocument,
      variables: { accessToken, refreshToken },
    })
    .catch((err) => {
      throw new Error(err);
    });

  return res?.data?.token;
  } else {
    throw new Error("refresh err")
  }

};

const promiseToObservable = <T>(promise: Promise<T>, fn: (value: T) => void) =>
  new Observable<T>((subscriber: any) => {
    promise
      .then(
        (value) => {
          if (subscriber.closed) return;
          fn(value);
          subscriber.next(value);
          subscriber.complete();
        },
        (err) => {
          console.log(err);
          wsService.disconnect();
          clearLs();
          window.location.reload();
          return subscriber.error(err);
        },
      )
      .finally(() => {
        isRefreshing = false;
      });
  });

export const completeTokens = ({
  accessToken,
  refreshToken,
}: TokenMutation['token']) => {
  localStorage.setItem('@accessToken', accessToken);
  localStorage.setItem('@refreshToken', refreshToken);
  wsService.refreshSockets();
};

const REFRESHED_ERRORS = [
  'Authorization token is not valid',
  'Session is not active',
  'JWT check error: JWT is not valid',
  'JWT is not valid',
  'jwt expired',
  'Unauthorized',
];

const errorLink = onError(
  ({ graphQLErrors, operation, forward, networkError }) => {
    const hasRefreshError = graphQLErrors?.some((error) =>
      REFRESHED_ERRORS.includes(error.message),
    );

    if (hasRefreshError) {
      if (!isRefreshing) {
        isRefreshing = true;
        return promiseToObservable(getNewToken(), completeTokens).flatMap(
          ({ accessToken }) => {
            const oldHeaders = operation.getContext().headers;
            operation.setContext({
              headers: {
                ...oldHeaders,
                authorization: `Bearer ${accessToken}`,
              },
            });
            return forward(operation);
          },
        );
      }

      return;
    }

    const reportableGraphQLErrors =
      graphQLErrors?.filter((error) => !isNullabilityError(error)) ?? [];

    reportableGraphQLErrors.forEach((error) => {
      logger.error(error, {
        operationName: operation.operationName,
        path: error.path?.join('.'),
        code: error.extensions?.code,
      });
    });

    if (networkError) {
      logger.error(networkError, {
        operationName: operation.operationName,
      });
    }
  },
);

export const client = new ApolloClient({
  cache: new InMemoryCache(),
  link: from([errorLink, retryLink, authMiddleware, httpLink]),
  defaultOptions: {
    query: {
      fetchPolicy: 'no-cache',
    },
    mutate: {
      fetchPolicy: 'network-only',
    },
  },
});
